var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ConsentInviteView, resolveConsentInviteContext, unwrapCortexResponse, } from 'libs.consent.react';
import { ApiClientService, AuthContext, AuthRoute, useApiClient } from 'libs.react_internal';
import { buildParticipantConsentPath, extractPrimaryParticipantId, findEnvironmentIdByOrgCode, } from '../../helpers/consent_participant_path';
export const ConsentInvitePage = () => {
    const { studyId = '' } = useParams();
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { entityId, isLoading: isAuthLoading, isLoggedIn, logout, user } = useContext(AuthContext);
    const momClient = useApiClient(ApiClientService.MOM);
    const identityId = user === null || user === void 0 ? void 0 : user.id;
    const [account, setAccount] = useState();
    // Where to send the signer once the single-use invite is done. Resolved lazily
    // to the participant's Consent tab; falls back to the app default landing.
    const [consentListPath, setConsentListPath] = useState(null);
    const inviteContext = useMemo(() => resolveConsentInviteContext({
        token: searchParams.get('code') || '',
        orgCode: searchParams.get('org') || '',
        environmentKey: searchParams.get('env') || '',
        routeEnvironmentId: Number(studyId),
    }), [searchParams, studyId]);
    const loginPath = useMemo(() => {
        const redirect = `${window.location.origin}${location.pathname}${location.search}`;
        const params = new URLSearchParams({ redirect });
        return `${AuthRoute.LOGIN}?${params.toString()}`;
    }, [location.pathname, location.search]);
    // Resolve the participant's Consent tab so the signer lands on the consent
    // list (not the consumed invite route) after signing. Needs the participant
    // off the signed document and the MOM environment id for this org code. Any
    // failure is non-fatal: the close handler falls back to the app default.
    useEffect(() => {
        if (!inviteContext || !(account === null || account === void 0 ? void 0 : account._id) || !entityId || !identityId) {
            return;
        }
        let isActive = true;
        const resolveConsentListPath = () => __awaiter(void 0, void 0, void 0, function* () {
            var _a;
            try {
                // Read the signed document via the same client key the working modal
                // uses (`accountApi` → MedableIAMDefaultClientKey). `c_studio_app` is
                // scoped to the eConsent script routes and rejects this object route,
                // which would silently strand the signer on the app default landing.
                const [signedDocResponse, environmentsResponse] = yield Promise.all([
                    inviteContext.accountApi.get(`/v2/account/${account._id}/ec__site_documents/${inviteContext.invite.ec__signed_document}`, { params: { paths: ['ec__primary_participant'], passive: true } }),
                    momClient.get(`/v1/entities/${entityId}/identities/${identityId}/environmentSites?page=1&limit=100`),
                ]);
                const participantId = extractPrimaryParticipantId(unwrapCortexResponse(signedDocResponse));
                const environmentId = findEnvironmentIdByOrgCode((_a = environmentsResponse === null || environmentsResponse === void 0 ? void 0 : environmentsResponse.data) === null || _a === void 0 ? void 0 : _a.data, inviteContext.orgCode);
                if (isActive && participantId && typeof environmentId === 'number') {
                    setConsentListPath(buildParticipantConsentPath(environmentId, participantId));
                }
            }
            catch (_b) {
                // Non-fatal: leave consentListPath unset so the close handler falls back to '/'.
            }
        });
        void resolveConsentListPath();
        return () => {
            isActive = false;
        };
    }, [account === null || account === void 0 ? void 0 : account._id, entityId, identityId, inviteContext, momClient]);
    const handleSwitchAccount = () => __awaiter(void 0, void 0, void 0, function* () {
        yield logout();
        navigate(loginPath, { replace: true });
    });
    // Closing the signing modal must leave the single-use invite route; staying on
    // `/consent-invite` re-validates the (now consumed) token. Send the signer to
    // the participant's Consent tab when we resolved it, otherwise the app default.
    const handleClose = () => {
        navigate(consentListPath || '/', { replace: true });
    };
    return (_jsx(ConsentInviteView, { inviteContext: inviteContext, isAuthLoading: isAuthLoading, isLoggedIn: isLoggedIn, userEmail: user === null || user === void 0 ? void 0 : user.email, onNeedsLogin: () => navigate(loginPath, { replace: true }), onAccountReady: setAccount, onSwitchAccount: handleSwitchAccount, onClose: handleClose }));
};
